import { Injectable } from '@angular/core';
import { EthersRequestService } from '@src/app/services/ethers-request.service';
import { RequestService } from '@src/app/services/request.service';
import { AddressNftHoldingV2, AddressProfile } from '@src/app/shared/types/address.type';
import { PageApiData } from '@src/app/shared/types/common.type';
import { Transaction, TransactionV2 } from '@src/app/shared/types/transaction.type';
import { ChainEnum } from '@src/app/shared/types/wallet-chain.type';
import { SupportedChain } from '../../shared/config/chain.config';
import { Holder, Holding20 } from '../../shared/types/address.type';

@Injectable()
export class AddressService {
    constructor(private requestService: RequestService, private ethersRequestService: EthersRequestService) {}

    getAddressProfile(address: string) {
        return this.requestService.sendRequest<AddressProfile>({
            method: 'GET',
            url: `/v1/socialscan/wallet/${address}/profile`,
        });
    }

    getAddressTransactionsV2(address: string, chain?: ChainEnum | 'all', pageState?: any) {
        let url = `/v1/socialscan/wallet/${address}/transactions`;
        if (pageState || chain) {
            url += '?';
            if (pageState) {
                url += `page_state=${pageState}`;
            }
            if (chain) {
                url += pageState ? `&chain=${chain}` : `chain=${chain}`;
            }
        }
        return this.requestService.sendRequest$<{ page_state: any; transactions: TransactionV2[] }>({
            method: 'GET',
            url,
        });
    }

    getNftHoldingsV2(address: string) {
        return this.requestService.sendRequest<{ holdings: AddressNftHoldingV2[] }>({
            method: 'GET',
            url: `/v2/socialscan/wallet/${address}/nft_token_holdings`,
        });
    }

    getFollowers(address: string, page = 1, size = 10) {
        return this.requestService.sendRequest<PageApiData<any>>({
            method: 'GET',
            url: `/v1/socialscan/wallet/${address}/followers?page=${page}&size=${size}`,
        });
    }

    getFollowings(address: string, page = 1, size = 10) {
        return this.requestService.sendRequest<PageApiData<any>>({
            method: 'GET',
            url: `/v1/socialscan/wallet/${address}/followings?page=${page}&size=${size}`,
        });
    }

    getAlsoViewed(address: string) {
        return this.requestService.sendRequest<
            {
                wallet_address: string;
                display_name: string;
                profile_image: string;
                ens_name: string;
                eth_balance: number;
                bnb_balance: number;
                polygon_balance: number;
                tags: string[];
                is_following: boolean;
                is_follower: boolean;
                allow_follower: boolean;
                followers_count: number;
                followings_count: number;
            }[]
        >({
            method: 'GET',
            url: `/v1/socialscan/wallet/recommendations?wallet=${address}`,
        });
    }

    getAddressTokenHoldings(address: string) {
        return Promise.all([
            this.requestService.sendRequest<{ holdings: Holding20[] }>({
                method: 'GET',
                url: `/v2/socialscan/wallet/${address}/token_holdings`,
            }),
            this.ethersRequestService.getAddressAllChainBalance(address),
        ]).then(data => {
            const [data1, data2] = data;

            data1.holdings.forEach(item => {
                if (!item.token_address) {
                    item.balance = Number(data2[item.chain_name]) || 0;
                }
            });

            return data1;
        });
    }

    getContractTransactions(data: { chain: string; contract_address: string; page?: number; size?: number; page_state?: string }) {
        return this.requestService.sendRequest<{
            page: number;
            size: number;
            total: number;
            page_state: string;
            data: TransactionV2[];
        }>({
            method: 'GET',
            url: `/v1/socialscan/contract/${data.chain}/${data.contract_address}/transactions${data.page_state ? `?page_state=${data.page_state}` : ''}`,
        });
    }

    getContractHolders(data: { chain: string; contract_address: string; page?: number; size?: number; type?: 'current' | 'ex' }) {
        return this.requestService.sendRequest<PageApiData<Holder>>({
            method: 'GET',
            url: `/v1/socialscan/contract/${data.chain}/${data.contract_address}/holders?page=${data.page || 1}&size=${data.size || 10}&type=${
                data.type || 'current'
            }`,
        });
    }
}
